import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../base/Modal";
import { Badge, Button, Tabs, confirmDialog, notifyError, notifySuccess } from "../ui";
import { useQuote } from "../../features/quotes/queries";
import { useGenerateQuotePdf, useUpdateQuoteStatus } from "../../features/quotes/mutations";
import type { Quote, QuoteStatus } from "../../features/quotes/types";
import { QuoteStatusBadge } from "./QuoteStatusBadge";
import { QuoteTotals, formatDuration, formatMoney } from "./QuoteTotals";
import { TollsEditor } from "./TollsEditor";
import { QuoteLinesTable } from "./QuoteLinesTable";

interface QuoteDetailModalProps {
  quoteId: string;
  initial?: Quote | null;
  onClose: () => void;
}

type Tab = "summary" | "breakdown" | "sends";

const NEXT_STATUS: Record<QuoteStatus, { status: QuoteStatus; labelKey: string; variant: "primary" | "successSoft" | "dangerSoft" | "outline" }[]> = {
  draft: [
    { status: "sent", labelKey: "quotes.actions.markSent", variant: "primary" },
    { status: "accepted", labelKey: "quotes.actions.markAccepted", variant: "successSoft" },
    { status: "rejected", labelKey: "quotes.actions.markRejected", variant: "dangerSoft" },
  ],
  sent: [
    { status: "accepted", labelKey: "quotes.actions.markAccepted", variant: "successSoft" },
    { status: "rejected", labelKey: "quotes.actions.markRejected", variant: "dangerSoft" },
  ],
  accepted: [],
  rejected: [{ status: "draft", labelKey: "quotes.actions.reopen", variant: "outline" }],
  expired: [{ status: "draft", labelKey: "quotes.actions.reopen", variant: "outline" }],
};

function fmtDate(v: Quote["createdAt"] | string | null | undefined): string {
  if (!v) return "—";
  const d = typeof v === "string" ? new Date(v) : "_seconds" in v ? new Date(v._seconds * 1000) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("es-MX") : "—";
}

/** Detalle de una cotización: resumen, desglose (sólo lectura), envíos (PR2) y acciones de estado/PDF. */
export function QuoteDetailModal({ quoteId, initial, onClose }: QuoteDetailModalProps) {
  const { t } = useTranslation("finanzasOperativas");
  const [tab, setTab] = useState<Tab>("summary");
  const q = useQuote(quoteId);
  const quote = q.data ?? initial ?? null;
  const statusMut = useUpdateQuoteStatus();
  const pdfMut = useGenerateQuotePdf();

  const changeStatus = async (status: QuoteStatus) => {
    if (!quote) return;
    if (status === "accepted") {
      const ok = await confirmDialog({ title: t("quotes.actions.markAccepted"), text: t("quotes.detail.locked"), tone: "primary" });
      if (!ok) return;
    }
    try {
      await statusMut.mutateAsync({ id: quote.id, status });
      void notifySuccess(t("quotes.toasts.statusUpdated"));
    } catch (err) {
      void notifyError(t("quotes.toasts.error"), (err as Error)?.message);
    }
  };

  const pdf = async () => {
    if (!quote) return;
    try {
      const meta = await pdfMut.mutateAsync({ id: quote.id, show_breakdown: quote.show_breakdown });
      if (meta?.url) window.open(meta.url, "_blank", "noopener");
    } catch (err) {
      void notifyError(t("quotes.toasts.error"), (err as Error)?.message);
    }
  };

  const place = (p: { address?: string; lat?: number; lng?: number } | null | undefined) =>
    p?.address || (p?.lat != null && p?.lng != null ? `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}` : "—");

  const tabs = [
    { id: "summary", label: t("quotes.detail.tabs.summary") },
    { id: "breakdown", label: t("quotes.detail.tabs.breakdown") },
    { id: "sends", label: t("quotes.detail.tabs.sends"), count: quote?.sends?.length ?? 0 },
  ];

  return (
    <Modal
      title={quote ? t("quotes.detail.title", { folio: quote.folio }) : t("common.loading")}
      onClose={onClose}
      width="w-full max-w-4xl"
      content={
        !quote ? (
          <div className="flex min-h-64 items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <QuoteStatusBadge status={quote.status} />
              {quote.title && <span className="text-sm font-medium text-content">{quote.title}</span>}
              <span className="flex-1" />
              {quote.pdf?.stale && (
                <Badge tone="warning" variant="outline" size="sm">
                  {t("quotes.detail.pdfStale")}
                </Badge>
              )}
              <Button variant="outline" size="sm" loading={pdfMut.isPending} icon={<i className="ri-file-pdf-2-line" />} onClick={() => void pdf()}>
                {quote.pdf?.url && !quote.pdf.stale ? t("quotes.result.openPdf") : t("quotes.result.generatePdf")}
              </Button>
            </div>

            <Tabs variant="underline" items={tabs} activeId={tab} onChange={(id) => setTab(id as Tab)} />

            {tab === "summary" && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
                <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:col-span-3 sm:grid-cols-2">
                  <Row k={t("quotes.fields.customer")} v={quote.customer_snapshot?.name || "—"} />
                  <Row k={t("quotes.fields.contact")} v={quote.customer_snapshot?.contact_name || quote.customer_snapshot?.email || quote.customer_snapshot?.whatsapp || "—"} />
                  <Row k={t("quotes.fields.origin")} v={place(quote.origin)} />
                  <Row k={t("quotes.fields.destination")} v={place(quote.destination)} />
                  <Row k={t("quotes.fields.vehicleClass")} v={t(`costProfiles.vehicleClasses.${quote.vehicle_class}`, { defaultValue: quote.vehicle_class })} />
                  <Row k={t("quotes.fields.unit")} v={quote.unit_snapshot ? [quote.unit_snapshot.name, quote.unit_snapshot.plate].filter(Boolean).join(" · ") : "—"} />
                  <Row k={t("quotes.fields.distance")} v={`${Number(quote.distance_km).toLocaleString("es-MX")} km`} />
                  <Row k={t("quotes.fields.duration")} v={`${formatDuration(quote.duration_min)} · ${quote.days} d`} />
                  <Row k={t("quotes.fields.pickupAt")} v={quote.schedule?.pickup_at ? new Date(quote.schedule.pickup_at).toLocaleString("es-MX") : "—"} />
                  <Row k={t("quotes.fields.deliveryAt")} v={quote.schedule?.delivery_at ? new Date(quote.schedule.delivery_at).toLocaleString("es-MX") : "—"} />
                  <Row k={t("quotes.fields.cargoDescription")} v={quote.cargo?.description || "—"} />
                  <Row k={t("quotes.fields.weightKg")} v={quote.cargo?.weight_kg ? `${quote.cargo.weight_kg.toLocaleString("es-MX")} kg` : "—"} />
                  <Row k={t("quotes.fields.validUntil")} v={fmtDate(quote.valid_until)} />
                  <Row k={t("quotes.fields.createdAt")} v={fmtDate(quote.createdAt)} />
                  {quote.notes && <Row k={t("quotes.fields.notes")} v={quote.notes} wide />}
                </dl>
                <div className="space-y-3 sm:col-span-2">
                  <QuoteTotals estimate={quote} />
                  {quote.status === "accepted" ? (
                    <p className="text-xs text-content-faint">{t("quotes.detail.locked")}</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {NEXT_STATUS[quote.status]?.map((a) => (
                        <Button key={a.status} variant={a.variant} size="sm" loading={statusMut.isPending} onClick={() => void changeStatus(a.status)}>
                          {t(a.labelKey)}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === "breakdown" && (
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-content">{t("quotes.calculation.tollsTitle")}</h4>
                  <TollsEditor tolls={quote.tolls} manualItems={null} onChange={() => {}} currency={quote.currency} readOnly />
                </div>
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-content">{t("quotes.calculation.linesTitle")}</h4>
                  <QuoteLinesTable lines={quote.lines} baseLines={quote.lines} overrides={{}} onChange={() => {}} currency={quote.currency} readOnly />
                </div>
                <div className="flex justify-end text-sm text-content-secondary">
                  {t("quotes.fields.costTotal")}: <span className="ml-2 font-semibold text-content">{formatMoney(quote.cost_total, quote.currency)}</span>
                </div>
              </div>
            )}

            {tab === "sends" && (
              <div className="min-h-32">
                {(quote.sends?.length ?? 0) === 0 ? (
                  <p className="py-8 text-center text-sm text-content-faint">{t("quotes.detail.noSends")}</p>
                ) : (
                  <ul className="divide-y divide-line text-sm">
                    {quote.sends!.map((s, i) => (
                      <li key={i} className="flex items-center gap-3 py-2">
                        <i className={s.channel === "email" ? "ri-mail-line" : "ri-whatsapp-line"} />
                        <span className="text-content">{s.to}</span>
                        <span className="ml-auto text-xs text-content-faint">{new Date(s.at).toLocaleString("es-MX")}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )
      }
    />
  );
}

function Row({ k, v, wide }: { k: string; v: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`flex flex-col ${wide ? "sm:col-span-2" : ""}`}>
      <dt className="text-xs text-content-faint">{k}</dt>
      <dd className="text-content">{v}</dd>
    </div>
  );
}
